import { Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X, ShoppingBag, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/chronicle", label: "Chronicle" },
  { to: "/sponsorship", label: "Sponsorship" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [collections, setCollections] = useState([]);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [mobileCollectionsOpen, setMobileCollectionsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const { count, openDrawer } = useCart();

  useEffect(() => {
    api
      .get("/collections")
      .then((r) => setCollections((r.data || []).filter((c) => c.product_count > 0)))
      .catch(() => setCollections([]));
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!collectionsOpen) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCollectionsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [collectionsOpen]);

  // Close menus on route change
  useEffect(() => {
    setOpen(false);
    setCollectionsOpen(false);
    setMobileCollectionsOpen(false);
  }, [location.pathname]);

  // Compute active state for each top-nav item, query-aware.
  // Shop is "active" only when the URL is exactly /shop with no lane.
  // Lane links are active when /shop?lane=<their lane>.
  const currentLane = new URLSearchParams(location.search).get("lane");
  const isItemActive = (to) => {
    if (to.startsWith("/shop?lane=")) {
      const target = new URL(to, "http://x").searchParams.get("lane");
      return location.pathname === "/shop" && currentLane === target;
    }
    if (to === "/shop") {
      return location.pathname === "/shop" && !currentLane;
    }
    return location.pathname === to;
  };

  const onCollectionsPath = location.pathname.startsWith("/collections/");

  return (
    <header
      className="sticky top-0 z-50 border-b-2 border-[#1A3626] bg-[#F9F9F4]/85 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-2 md:px-10 md:py-3">
        <Link
          to="/"
          className="group flex shrink-0 flex-col leading-none transition-opacity duration-300 hover:opacity-70"
          data-testid="nav-home-logo"
          aria-label="Wet Cabbage — home"
        >
          <span
            className="wc-wordmark-bold text-[22px] tracking-tight md:text-[28px] lg:text-[32px]"
            data-testid="nav-wordmark"
          >
            Wet Cabbage
          </span>
          <span
            className="font-display mt-1.5 text-[13px] font-bold uppercase tracking-[0.26em] text-[#1A3626]/90 md:text-[15px] md:tracking-[0.28em] lg:text-[17px]"
            data-testid="nav-sub-wordmark"
          >
            Streetwear For The Comfortably Unbothered
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {NAV.map((item, idx) => (
            <span key={item.to} className="flex items-center">
              <Link
                to={item.to}
                className={`font-display px-3 py-2 text-sm font-bold uppercase tracking-widest transition-colors ${
                  isItemActive(item.to)
                    ? "bg-[#1A3626] text-[#F9F9F4]"
                    : "text-[#0F0F0F] hover:bg-[#EBE7DD]"
                }`}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </Link>
              {/* Collections dropdown sits after The Campaign link (idx === 0) */}
              {idx === 0 && collections.length > 0 && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setCollectionsOpen((o) => !o)}
                    className={`font-display flex items-center gap-1 px-3 py-2 text-sm font-bold uppercase tracking-widest transition-colors ${
                      onCollectionsPath || collectionsOpen
                        ? "bg-[#1A3626] text-[#F9F9F4]"
                        : "text-[#0F0F0F] hover:bg-[#EBE7DD]"
                    }`}
                    aria-haspopup="menu"
                    aria-expanded={collectionsOpen}
                    data-testid="nav-collections-toggle"
                  >
                    Collections
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform ${
                        collectionsOpen ? "rotate-180" : ""
                      }`}
                      strokeWidth={2.6}
                    />
                  </button>
                  {collectionsOpen && (
                    <div
                      className="absolute left-0 top-full z-50 mt-1 w-[260px] border-2 border-[#1A3626] bg-[#F9F9F4] shadow-[6px_6px_0_0_#1A3626]"
                      role="menu"
                      data-testid="nav-collections-menu"
                    >
                      {collections.map((c) => (
                        <Link
                          key={c.id}
                          to={`/collections/${c.id}`}
                          onClick={() => setCollectionsOpen(false)}
                          className="font-display block border-b border-[#1A3626]/15 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors last:border-b-0 hover:bg-[#93C065] hover:text-[#0F0F0F]"
                          data-testid={`nav-collection-${c.id}`}
                        >
                          {c.title}
                          <span className="ml-2 font-body text-[10px] font-normal lowercase text-[#1A3626]/60">
                            {c.product_count} piece{c.product_count === 1 ? "" : "s"}
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </span>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openDrawer}
            className="relative flex h-10 w-10 items-center justify-center border-2 border-[#1A3626] bg-[#F9F9F4] transition-all hover:bg-[#93C065] md:h-11 md:w-11"
            aria-label="Open cart"
            data-testid="header-cart-button"
          >
            <ShoppingBag className="h-5 w-5" strokeWidth={2.6} />
            {count > 0 && (
              <span
                className="font-display absolute -right-2 -top-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full border-2 border-[#1A3626] bg-[#93C065] px-1 text-[10px] font-black text-[#0F0F0F]"
                data-testid="header-cart-count"
              >
                {count}
              </span>
            )}
          </button>

          <button
            className="md:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="border-t-2 border-[#1A3626] bg-[#F9F9F4] md:hidden"
          data-testid="mobile-nav"
        >
          <div className="flex flex-col">
            {NAV.map((item, idx) => (
              <span key={item.to} className="flex flex-col">
                <Link
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={`font-display border-b border-[#1A3626]/30 px-6 py-4 text-base font-bold uppercase tracking-widest ${
                    isItemActive(item.to) ? "bg-[#1A3626] text-[#F9F9F4]" : "text-[#0F0F0F]"
                  }`}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </Link>
                {idx === 0 && collections.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setMobileCollectionsOpen((o) => !o)}
                      className="font-display flex items-center justify-between border-b border-[#1A3626]/30 bg-[#EBE7DD] px-6 py-4 text-base font-bold uppercase tracking-widest text-[#0F0F0F]"
                      aria-expanded={mobileCollectionsOpen}
                      data-testid="mobile-nav-collections-toggle"
                    >
                      Collections
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          mobileCollectionsOpen ? "rotate-180" : ""
                        }`}
                        strokeWidth={2.6}
                      />
                    </button>
                    {mobileCollectionsOpen &&
                      collections.map((c) => (
                        <Link
                          key={c.id}
                          to={`/collections/${c.id}`}
                          onClick={() => setOpen(false)}
                          className="font-display border-b border-[#1A3626]/15 bg-[#F9F9F4] px-10 py-3 text-sm font-bold uppercase tracking-[0.2em]"
                          data-testid={`mobile-nav-collection-${c.id}`}
                        >
                          {c.title}
                        </Link>
                      ))}
                  </>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
